import { useState } from "react";
import { Header } from "../components/promill/Header";
import { Footer } from "../components/promill/Footer";
import { toast } from "sonner";
import { Building2, TrendingUp, Users, Sparkles, ArrowRight, Send, MessageCircle } from "lucide-react";
import { WHATSAPP_URL } from "../data/promill";

const REASONS = [
    { icon: TrendingUp, title: "High-growth category", desc: "India's protein & clean-food market is expected to 4x by 2030. Millets are the biggest tailwind." },
    { icon: Users, title: "Loyal community", desc: "Urban India's health-conscious 20–40 cohort loves ProMill — repeat rate over 60% at pilot outlets." },
    { icon: Sparkles, title: "Full-stack playbook", desc: "Store design, kitchen SOPs, menu R&D, digital ordering, marketing — we hand you the entire system." },
    { icon: Building2, title: "Flexible formats", desc: "500–1,000 sq ft cafe and restaurant footprints — pick the model that fits your city and site." },
];

const FranchisePage = () => {
    const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", investment: "", message: "" });
    const [sent, setSent] = useState(false);
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone || !form.city) {
            toast.error("Please fill your name, email, phone and city.");
            return;
        }
        try {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/franchise`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            if (!res.ok) throw new Error("Network");
            setSent(true);
            toast.success("Application received. Our franchise team will contact you within 48 hours.");
            setForm({ name: "", email: "", phone: "", city: "", investment: "", message: "" });
        } catch (_e) {
            toast.error("Something went wrong. Please try WhatsApp instead.");
        }
    };

    return (
        <div data-testid="franchise-page" className="bg-pm-cream min-h-screen">
            <Header />

            {/* Hero */}
            <section className="pt-10 md:pt-16 pb-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
                    <div>
                        <span className="inline-block text-pm-orange font-bold tracking-[0.25em] uppercase text-[11px] mb-3">
                            Franchise Opportunity
                        </span>
                        <h1 data-testid="franchise-headline" className="text-4xl md:text-6xl font-extrabold tracking-[-0.02em] text-pm-black leading-[1.05]">
                            Own the future of <span className="text-pm-green">Indian eating.</span>
                        </h1>
                        <p className="mt-5 text-base md:text-lg text-pm-black/70 leading-relaxed max-w-lg">
                            Partner with ProMill to open a franchise outlet in your city. Backed by a modern
                            brand, a proven kitchen playbook, and a fast-growing millet-first movement.
                        </p>
                        <div className="mt-7 flex flex-wrap gap-3">
                            <a href="#apply" data-testid="franchise-apply-scroll" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-pm-yellow text-pm-black font-bold hover:bg-pm-yellowBright transition-colors">
                                Apply Now
                                <ArrowRight className="w-4 h-4" />
                            </a>
                            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-pm-black/15 font-bold hover:bg-white">
                                <MessageCircle className="w-4 h-4" />
                                WhatsApp Us
                            </a>
                        </div>
                    </div>
                    <div className="bg-pm-green text-white rounded-3xl p-8 md:p-10 space-y-5">
                        <h3 className="text-pm-yellow font-bold tracking-[0.2em] uppercase text-xs">Snapshot</h3>
                        <div className="grid grid-cols-2 gap-5">
                            <div><div className="text-4xl font-extrabold text-pm-yellow">500–1,000</div><div className="text-sm text-white/75 mt-1">sq ft space</div></div>
                            <div><div className="text-4xl font-extrabold text-pm-yellow">₹60–90L</div><div className="text-sm text-white/75 mt-1">est. investment</div></div>
                            <div><div className="text-4xl font-extrabold text-pm-yellow">12–18</div><div className="text-sm text-white/75 mt-1">months payback*</div></div>
                            <div><div className="text-4xl font-extrabold text-pm-yellow">Tier 1–2</div><div className="text-sm text-white/75 mt-1">cities open</div></div>
                        </div>
                        <div className="pt-4 border-t border-white/15 grid sm:grid-cols-2 gap-4">
                            <div>
                                <div className="text-xs uppercase tracking-widest text-pm-yellow font-bold">Cafe Model</div>
                                <div className="mt-1 text-2xl font-extrabold text-white">₹60–70 Lakhs</div>
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-widest text-pm-yellow font-bold">Restaurant Model</div>
                                <div className="mt-1 text-2xl font-extrabold text-white">₹80–90 Lakhs</div>
                            </div>
                        </div>
                        <p className="text-xs text-white/60">*Indicative. Final numbers subject to location, format and partner discussion.</p>
                    </div>
                </div>
            </section>

            {/* Why franchise */}
            <section className="py-14 md:py-20 bg-white border-y border-pm-black/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-2xl mb-10">
                        <span className="text-pm-orange font-bold tracking-[0.25em] uppercase text-[11px]">Why Partner With Us</span>
                        <h2 className="mt-3 text-3xl md:text-5xl font-extrabold tracking-[-0.02em] text-pm-black leading-tight">
                            Ride the millet wave <span className="text-pm-green">early.</span>
                        </h2>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {REASONS.map((r, i) => (
                            <div key={i} data-testid={`franchise-reason-${i}`} className="p-6 rounded-2xl border border-pm-black/8 bg-pm-cream card-lift">
                                <div className="h-12 w-12 rounded-full bg-pm-yellow/20 flex items-center justify-center mb-4">
                                    <r.icon className="w-6 h-6 text-pm-green" strokeWidth={1.7} />
                                </div>
                                <h3 className="text-base font-extrabold text-pm-black">{r.title}</h3>
                                <p className="mt-2 text-sm text-pm-black/65 leading-relaxed">{r.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Application form */}
            <section id="apply" data-testid="franchise-apply-section" className="py-14 md:py-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-10">
                        <span className="text-pm-orange font-bold tracking-[0.25em] uppercase text-[11px]">Apply</span>
                        <h2 className="mt-3 text-3xl md:text-5xl font-extrabold tracking-[-0.02em] text-pm-black leading-tight">
                            Start your <span className="text-pm-green">ProMill journey.</span>
                        </h2>
                        <p className="mt-3 text-pm-black/65 max-w-xl mx-auto">Tell us about you and your city. Our franchise team responds within 48 hours.</p>
                    </div>
                    <form onSubmit={submit} data-testid="franchise-form" className="bg-white rounded-2xl border border-pm-black/8 p-6 md:p-8 space-y-5">
                        <div className="grid sm:grid-cols-2 gap-5">
                            <Field label="Name *" testId="fr-name" value={form.name} onChange={set("name")} required />
                            <Field label="Email *" testId="fr-email" type="email" value={form.email} onChange={set("email")} required />
                            <Field label="Phone *" testId="fr-phone" value={form.phone} onChange={set("phone")} required />
                            <Field label="City *" testId="fr-city" value={form.city} onChange={set("city")} required />
                        </div>
                        <label className="block">
                            <span className="text-xs uppercase tracking-widest font-bold text-pm-black/60">Investment capacity</span>
                            <select value={form.investment} onChange={set("investment")} data-testid="fr-investment" className="mt-2 w-full h-12 px-4 rounded-xl border border-pm-black/10 bg-white focus:outline-none focus:border-pm-green">
                                <option value="">Select model</option>
                                <option value="Cafe Model (₹60-70L)">Cafe Model — ₹60–70 Lakhs</option>
                                <option value="Restaurant Model (₹80-90L)">Restaurant Model — ₹80–90 Lakhs</option>
                            </select>
                        </label>
                        <label className="block">
                            <span className="text-xs uppercase tracking-widest font-bold text-pm-black/60">Message</span>
                            <textarea rows={4} value={form.message} onChange={set("message")} data-testid="fr-message" className="mt-2 w-full px-4 py-3 rounded-xl border border-pm-black/10 focus:outline-none focus:border-pm-green" />
                        </label>
                        <button type="submit" data-testid="fr-submit" className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-pm-green text-pm-yellow font-bold hover:bg-pm-black transition-colors">
                            Submit Application
                            <Send className="w-4 h-4" />
                        </button>
                        {sent && <div data-testid="franchise-success" className="text-sm text-pm-green font-bold">Application received. Talk soon.</div>}
                    </form>
                </div>
            </section>

            <Footer />
        </div>
    );
};

const Field = ({ label, testId, ...props }) => (
    <label className="block">
        <span className="text-xs uppercase tracking-widest font-bold text-pm-black/60">{label}</span>
        <input {...props} data-testid={testId} className="mt-2 w-full h-12 px-4 rounded-xl border border-pm-black/10 focus:outline-none focus:border-pm-green focus:ring-2 focus:ring-pm-yellow/30" />
    </label>
);

export default FranchisePage;
